/**
 * Per-edge polygon offsetting (SD-4.2, FR-10, AD-26).
 *
 * The algorithm is the FR-36 spike's attempt 2, the only one that survived a concave face:
 * offset each edge LINE by its own distance, rebuild vertices from consecutive offset-line
 * intersections, then resolve the self-intersections that appear at reflex vertices with a
 * boolean union, keeping only the lobes the raw ring winds around in the face's own orientation.
 *
 * NEVER half-plane intersection. An intersection of half-planes is always convex, so on any
 * concave face it returns an empty region without erroring — both convex test faces pass, the
 * dormer dies silently. That is the bug the spike existed to catch; `offset.test.ts` pins it.
 *
 * Known limitation, inherent to offsetting LINES rather than segments: when two opposing edges
 * are exactly collinear after offsetting (a notch from the top and a notch from the bottom with
 * perfectly aligned walls, at insets that meet), the resolved ring can keep a thin bridging
 * strip that sits within the setback of both edges instead of pinching cleanly. The
 * configuration is knife-edge (any stagger of the walls resolves it, see DUMBBELL_FACE) and the
 * error is a sliver, but it is a real deviation from segment-distance semantics and is stated
 * rather than hidden.
 */

import { resolveSelfIntersections } from './booleans';
import { interiorPoint, signedArea, windingNumber } from './predicates';
import type { Pt } from './types';

/** Distance from a point to a SEGMENT (not to its infinite line). */
function distanceToSegment(p: Pt, a: Pt, b: Pt): number {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const len2 = vx * vx + vy * vy;
  if (len2 === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  let t = ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p[0] - (a[0] + t * vx), p[1] - (a[1] + t * vy));
}

/**
 * Drop regions that violate segment-distance semantics (SD-4.3).
 *
 * THE BUG THIS CLOSES, found by SD-4.3 as the first real consumer. This function's own contract
 * says "setbacks that consume the whole face return []", and it did not. Offsetting LINES means
 * that when the insets exceed the face, opposing lines cross over each other and the rebuilt ring
 * is a perfectly well-formed polygon on the WRONG side of its own edges. A 10 m square inset 6 m
 * per edge returned a tidy 2×2 box at (4,4)-(6,6) — inside the original face, correctly wound,
 * and entirely within the setback of all four edges.
 *
 * That is worse than an error: the envelope draws a box that is not real, and the packer places
 * panels in a zone a permit review would reject. It bites hardest on small faces with generous
 * setbacks — a dormer with a 24 in eave — which are exactly the faces where the setback decides
 * the answer.
 *
 * The filter is a segment-distance check, NOT the half-plane test this module warns against.
 * The distinction matters: half-plane INTERSECTION is invalid for CONSTRUCTION on a concave face
 * because a valid lobe can legitimately sit on the outer side of a distant edge's infinite line.
 * Distance to the SEGMENT has no such failure — a point in one arm of an L is genuinely far from
 * the other arm's segment — so it is sound as a validation filter on an already-built region.
 */
function withinSegmentDistances(
  region: readonly Pt[],
  face: readonly Pt[],
  distances: readonly number[]
): boolean {
  const n = face.length;
  // Generous tolerance: vertices sit EXACTLY at their own edge's distance, so the comparison is
  // against floating-point equality. This is not a fudge factor for near-misses — the failures it
  // must catch are off by whole metres.
  const EPS = 1e-6;
  for (const v of region) {
    for (let i = 0; i < n; i++) {
      const d = distances[i] ?? 0;
      if (d <= 0) continue;
      if (distanceToSegment(v, face[i], face[(i + 1) % n]) < d - EPS) return false;
    }
  }
  return true;
}

/** Intersection of two infinite lines given as point + direction; null when parallel. */
function lineIntersect(p1: Pt, d1: Pt, p2: Pt, d2: Pt): Pt | null {
  const den = d1[0] * d2[1] - d1[1] * d2[0];
  if (Math.abs(den) < 1e-12) return null;
  const t = ((p2[0] - p1[0]) * d2[1] - (p2[1] - p1[1]) * d2[0]) / den;
  return [p1[0] + t * d1[0], p1[1] + t * d1[1]];
}

/**
 * Inset `face` by `distances[i]` along edge i (face[i] → face[i+1]), in metres.
 *
 * Returns an ARRAY of regions, because offsetting can legitimately pinch one face into several
 * placeable areas. Setbacks that consume the whole face return `[]`. Zero setbacks on every edge
 * return the original face. `distances` must have exactly one entry per edge — the index mapping
 * IS the per-edge contract, which is also why the face is offset in its own vertex order rather
 * than normalised to CCW first: reversing would silently remap every distance.
 */
export function offsetPolygonPerEdge(face: readonly Pt[], distances: readonly number[]): Pt[][] {
  if (!face || face.length < 3) return [];
  if (distances.length !== face.length) {
    throw new Error(
      `offsetPolygonPerEdge: ${face.length} edges but ${distances.length} distances — one per edge`,
    );
  }
  if (
    face.some((p) => !Number.isFinite(p?.[0]) || !Number.isFinite(p?.[1])) ||
    distances.some((d) => !Number.isFinite(d))
  ) {
    return [];
  }
  if (distances.every((d) => d === 0)) {
    // The original face, verbatim — not a round-trip through the union.
    return [face.map((p): Pt => [p[0], p[1]])];
  }

  const n = face.length;
  // +1 CCW, -1 CW: picks the inward normal so the caller's winding doesn't matter.
  const orient = Math.sign(signedArea(face)) || 1;

  const lines: Array<{ pt: Pt; dir: Pt }> = [];
  for (let i = 0; i < n; i += 1) {
    const a = face[i];
    const b = face[(i + 1) % n];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const nx = (orient > 0 ? -dy : dy) / len;
    const ny = (orient > 0 ? dx : -dx) / len;
    const d = distances[i];
    lines.push({ pt: [a[0] + nx * d, a[1] + ny * d], dir: [dx, dy] });
  }

  // Rebuild each vertex as the intersection of its two adjacent offset lines. Parallel
  // neighbours (collinear edges) fall back to the current line's base point.
  const raw: Pt[] = [];
  for (let i = 0; i < n; i += 1) {
    const prev = lines[(i - 1 + n) % n];
    const cur = lines[i];
    raw.push(lineIntersect(prev.pt, prev.dir, cur.pt, cur.dir) ?? cur.pt);
  }

  // The union resolves self-intersections but normalises winding, so it cannot tell a genuine
  // inset lobe from a face turned inside-out by setbacks larger than the face. The raw ring can:
  // it winds in the face's own orientation around real lobes and the other way around inverted
  // ones. Filter by that.
  return resolveSelfIntersections(raw)
    .filter((lobe) => windingNumber(interiorPoint(lobe), raw) * orient > 0)
    // SD-4.3: the winding test alone is not enough. When the insets exceed the face, opposing
    // offset LINES cross and the resulting ring is correctly wound around a region that sits
    // entirely inside its own setbacks — a 10 m square inset 6 m per edge survived this filter as
    // a tidy 2×2 box. See withinSegmentDistances.
    .filter((lobe) => withinSegmentDistances(lobe, face, distances));
}
